import React, { useState } from 'react';
import api from '../services/api';
import AddressSuggestInput from './AddressSuggestInput';
import {
  DEFAULT_OBSERVABLE_TYPE,
  INDICATOR_CONFIDENCE_LEVELS,
  OPENCTI_OBSERVABLE_TYPES,
  observableTypeLabel,
} from '../config/openctiObservables';

function guessObservableType(value) {
  const v = (value || '').trim();
  if (!v) return DEFAULT_OBSERVABLE_TYPE;
  if (/^(\+?[\d\s().-]{7,}|0\d{9,})$/.test(v)) return 'Phone-Number';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(v)) return 'IPv4-Addr';
  if (/:/.test(v) && /[0-9a-fA-F]/.test(v) && v.includes(':')) return 'IPv6-Addr';
  if (/^https?:\/\//i.test(v) || /^www\./i.test(v)) return 'Url';
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return 'Email-Addr';
  if (/^(0x)?[a-fA-F0-9]{40,}$/.test(v) || /^[13][a-km-zA-HJ-NP-Z1-9]{25,}|^bc1/.test(v)) {
    return 'Cryptocurrency-Wallet';
  }
  if (/^[a-fA-F0-9]{32}$|^[a-fA-F0-9]{40}$|^[a-fA-F0-9]{64}$/.test(v)) return 'File';
  if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(v)) return 'Domain-Name';
  return DEFAULT_OBSERVABLE_TYPE;
}

/**
 * Compact create form for a new OpenCTI-typed indicator.
 * Uses a div (not <form>) so it can nest inside AddRelationshipForm safely.
 */
function CreateIndicatorFields({
  initialValue = '',
  initialType = DEFAULT_OBSERVABLE_TYPE,
  caseId,
  actorId,
  onCreated,
  onCancel,
  disabled = false,
  submitLabel = 'Create indicator',
}) {
  const [form, setForm] = useState({
    value: initialValue,
    ioc_type: initialType || guessObservableType(initialValue),
    confidence: 'medium',
    notes: '',
  });
  const [addressMeta, setAddressMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isPhysicalAddress = form.ioc_type === 'Physical-Address';

  const submit = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (disabled || saving || !form.value.trim()) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        value: form.value.trim(),
        ioc_type: form.ioc_type,
        confidence: form.confidence,
        notes: form.notes.trim() || undefined,
      };
      if (caseId) payload.case_id = caseId;
      if (actorId) payload.actor_id = actorId;
      if (isPhysicalAddress && addressMeta) {
        payload.metadata = {
          geocode: {
            source: addressMeta.source || 'nominatim',
            lat: addressMeta.lat,
            lon: addressMeta.lon,
            osm_id: addressMeta.osm_id,
            osm_type: addressMeta.osm_type,
            countrycode: addressMeta.countrycode,
            housenumber: addressMeta.housenumber,
            street: addressMeta.street,
            city: addressMeta.city,
            county: addressMeta.county,
            state: addressMeta.state,
            postcode: addressMeta.postcode,
            country: addressMeta.country,
          },
        };
      }
      const res = await api.post('/hdp/iocs', payload);
      const ioc = res.data;
      if (!ioc?.indicator_id) {
        throw new Error('Indicator created but response was missing indicator_id');
      }
      onCreated?.(ioc);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create indicator');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="create-indicator-fields">
      <div className="create-indicator-grid">
        <div>
          <label className="form-label">Observable type</label>
          <select
            className="form-input"
            value={form.ioc_type}
            onChange={(e) => {
              const next = e.target.value;
              setForm((prev) => ({ ...prev, ioc_type: next }));
              if (next !== 'Physical-Address') setAddressMeta(null);
            }}
            disabled={disabled || saving}
          >
            {OPENCTI_OBSERVABLE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">Value</label>
          {isPhysicalAddress ? (
            <AddressSuggestInput
              value={form.value}
              disabled={disabled || saving}
              placeholder="Start typing an address…"
              onChange={(next) => setForm((prev) => ({ ...prev, value: next }))}
              onSelect={setAddressMeta}
            />
          ) : (
            <input
              className="form-input"
              value={form.value}
              onChange={(e) => setForm((prev) => ({ ...prev, value: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  submit(e);
                }
              }}
              placeholder="e.g. 555-0100, evil.example, 1.2.3.4"
              required
              disabled={disabled || saving}
            />
          )}
        </div>
        <div>
          <label className="form-label">Confidence</label>
          <select
            className="form-input"
            value={form.confidence}
            onChange={(e) => setForm((prev) => ({ ...prev, confidence: e.target.value }))}
            disabled={disabled || saving}
          >
            {INDICATOR_CONFIDENCE_LEVELS.map((level) => (
              <option key={level} value={level}>{level}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group" style={{ marginTop: '8px' }}>
        <label className="form-label">Notes</label>
        <input
          className="form-input"
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Optional"
          disabled={disabled || saving}
        />
      </div>
      {error && <p className="relationship-composer-error">{error}</p>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={disabled || saving || !form.value.trim()}
          onClick={submit}
        >
          {saving ? 'Creating…' : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
      </div>
      <p className="create-indicator-hint">
        {isPhysicalAddress
          ? 'Address suggestions from OpenStreetMap. Creating one also links City, State, Postal Code, Street, County, and Country as separate observables.'
          : `Type: ${observableTypeLabel(form.ioc_type)} (OpenCTI observable)`}
      </p>
    </div>
  );
}

export default CreateIndicatorFields;
